/**
 * kanbanUtils.js
 *
 * Utility functions for creating and rendering project field columns in a Kanban board.
 * These functions manage the dynamic rendering of different field types (e.g., DATE, STATUS, NUMBER, STRING) 
 * and provide the necessary editors for various project tasks.
 *
 * The main exported function is:
 * - `createColumns`: Generates and configures columns for project fields, with logic to render different field types (e.g., DATE, STATUS) and their respective editors.
 */

import {
  DateValueEditor,
  DurationValueEditor,
  NumberValueEditor,
  StatusValueEditor,
  StringValueEditor,
} from "../../../pages/ProjectTaskPage/ValueEditors";
import { renderDateCellValue } from "../helper";
import { ColumnTypes } from "./columnUtils";

/**
 * Returns the width configuration for a given project field based on its type.
 * 
 * @param {Object} field - The project field object that contains the type.
 * @param {ColumnTypes} field.type - The type of the column (e.g., TIME_REPORT, STATUS, etc.).
 * @returns {string} The CSS width configuration for the column.
 */
const getProjectFieldColumnWidth = (field) => {
  switch (field.type) {
    case ColumnTypes.TIME_REPORT: return "auto"; // Duration
    case ColumnTypes.STATUS: return "minmax(50px,150px)";
    case ColumnTypes.DATE: return "auto";
    case ColumnTypes.NUMBER: return "auto";
    case ColumnTypes.STRING: return "209px";
    default:
      console.warn("** unhandled type: " + field.type);
      return "auto"; // Fallback width
  }
};

/**
 * Creates column configurations for a list of project fields.
 * 
 * This function takes an array of project fields, processes them, and returns a list of column objects.
 * If `jsonFieldSettings` is provided, it attempts to parse and apply those settings to the field.
 * 
 * @param {Array} projectFields - Array of project field objects that should be used to create columns.
 * @returns {Array} Array of column configurations, each containing:
 *   - `fieldId` {string} - The unique identifier for the column.
 *   - `label` {string} - The label for the column.
 *   - `renderCell` {function} - Function to render the cell content based on the field type.
 *   - `type` {ColumnTypes} - The column type (e.g., STRING, DATE, etc.).
 *   - `width` {string} - CSS width for the column.
 */
const createColumns = (projectFields) => {
  if (!Array.isArray(projectFields) || projectFields.length === 0) {
    return [];
  }

  return projectFields.map((projectField) => {
    const { fieldId, name, type, jsonFieldSettings } = projectField;

    let field = { ...projectField };

    // Parsing and applying field settings from jsonFieldSettings (if available)
    if (jsonFieldSettings) {
      try {
        const settings = JSON.parse(jsonFieldSettings);
        field = { ...field, settings };
      } catch (error) {
        console.error(`Error parsing settings for projectField: ${field}`, error);
      }
    }

    return {
      fieldId,
      label: name,
      renderCell: (item, onChange, enableEdit, inline, disabled, onValidationChange) =>
        renderProjectFieldCell(enableEdit, field, item, onChange, inline, disabled, onValidationChange),
      type,
      width: getProjectFieldColumnWidth(field),
    };
  });
};

/**
 * Renders the cell content for a specific project field.
 * 
 * This function calls the appropriate render function for the field's value based on its type,
 * and provides the necessary parameters for editing or displaying the value.
 * 
 * @param {boolean} enableEdit - Determines if the field is in edit mode.
 * @param {Object} field - The project field configuration.
 * @param {Object} item - The current item (e.g., task) containing field values.
 * @param {function} onChange - Callback function to handle value change.
 * @param {boolean} inline - Determines if the editor should be rendered inline.
 * @param {boolean} disabled - Determines if the editor is disabled.
 * @param {function} onValidationChange - Callback to handle validation status change.
 * @returns {React.Element} The rendered cell value.
 */
const renderProjectFieldCell = (enableEdit, field, item, onChange, inline, disabled, onValidationChange) => {
  const displayValue = item.values.find(n => n.fieldId === field.fieldId);

  return renderValueCell(enableEdit, field, displayValue, onChange, inline, disabled, onValidationChange);
};

/**
 * Renders a date cell based on the field configuration and the provided value.
 * 
 * This function determines whether the field should be editable. If so, it renders a date editor component.
 * If the field is not in edit mode, it renders the date value as plain text.
 * 
 * @param {boolean} enableEdit - Determines if the field is in edit mode (true) or view-only mode (false).
 * @param {Object} field - The field configuration object containing details like field type and settings.
 * @param {Object} value - The current value of the field, which should be a date object or a string representing a date.
 * @param {function} onChange - Callback function to handle updates to the field's value when edited.
 * @param {function} onValidationChange - Callback function to notify about changes in the validation status of the field.
 * 
 * @returns {React.Element} A date editor component if `enableEdit` is true, otherwise a rendered date value.
 */
const renderDateValueCell = (enableEdit, field, value, onChange, onValidationChange) => {
  return enableEdit
    ? <DateValueEditor
      field={field}
      value={value}
      onUpdateTaskValue={onChange}
      onValidationChange={(valid) => onValidationChange?.(field.fieldId, valid)}
    />
    : renderDateCellValue(value);
};
/**
 * Renders a value cell based on the field type.
 * 
 * This function dynamically renders the correct editor or display component based on the field's type,
 * such as displaying a number editor for `NUMBER`, a date picker for `DATE`, or a dropdown for `STATUS`.
 * 
 * @param {boolean} enableEdit - Determines if the field should be editable.
 * @param {Object} field - The field configuration that determines the type and other settings.
 * @param {Object} value - The current value for this field.
 * @param {function} onChange - Callback to handle changes to the value.
 * @param {boolean} inline - Determines if the editor should be rendered inline or in a separate modal.
 * @param {boolean} disabled - Whether the editor should be disabled.
 * @param {function} onValidationChange - Callback to handle changes in field validation status.
 * @returns {React.Element} The editor component or display value.
 */
const renderValueCell = (enableEdit, field, value, onChange, inline, disabled, onValidationChange) => {
  switch (field.type) {
    case ColumnTypes.TIME_REPORT: // Duration
      return (
        <DurationValueEditor enableEdit={enableEdit} field={field} value={value} onUpdateTaskValue={onChange} sx={{ justifyContent: (inline ? "flex-end" : "flex-start") }} />
      );

    case ColumnTypes.STATUS:
      return (
        <StatusValueEditor disabled={disabled} enableEdit={enableEdit} field={field} hideUnassigned={inline} onUpdateTaskValue={onChange} value={value} />
      );

    case ColumnTypes.DATE:
      return renderDateValueCell(enableEdit, field, value, onChange, onValidationChange);

    case ColumnTypes.NUMBER:
      return (
        <NumberValueEditor disabled={disabled} enableEdit={enableEdit} field={field} inline={inline} value={value} onUpdateTaskValue={onChange} />
      );

    case ColumnTypes.STRING:
      return (
        <StringValueEditor disabled={disabled} enableEdit={enableEdit} field={field} inline={inline} value={value} onUpdateTaskValue={onChange} />
      );

    default:
      return "Type: " + field.type + ":" + value || "No Value";
  }
};

export { createColumns };
